import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ExportItem {
  [key: string]: string | number | boolean | null | undefined;
}

export class SimplePDFService {
  // Export to PDF
  public exportToPDF(data: ExportItem[], fileName: string, title: string): void {
    try {
      console.log("=== PDF EXPORT SERVICE ===");
      console.log("PDF Export - Data Size:", data.length);
      
      // Early validation
      if (!data || data.length === 0) {
        console.error("PDF Export - No data to export");
        return;
      }
      
      // Inspect the first data item
      console.log("PDF Export - First data item:", JSON.stringify(data[0], null, 2));
      
      // Create a new PDF document
      const doc = new jsPDF({
        orientation: 'landscape', // Use landscape orientation for wider tables
      });
      
      // Add title
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      
      // We want to control the order of the columns to match the UI
      // Standard column order: Keyword, Volume, KD, CPC, Intent, followed by domains
      let orderedHeaders: string[] = [];
      const standardColumns = ["keyword", "volume", "kd", "cpc", "intent"];
      
      if (data.length > 0) {
        const allKeys = Object.keys(data[0]);

        // Merge standard + remaining keys (no duplicates)
        orderedHeaders = [
          ...standardColumns.filter(col => allKeys.includes(col)),
          ...allKeys.filter(key => !standardColumns.includes(key))
        ];

        // Filter only columns that have real values
        orderedHeaders = orderedHeaders.filter((key) =>
          data.some((row) => {
            const value = row[key];
            return value !== null && value !== undefined && value !== "" && value !== "—";
          })
        );

        console.log("PDF Export - Final headers:", orderedHeaders);
      } else {
        orderedHeaders = standardColumns;
      }
      
      // Format the header names to be more readable
      const displayHeaders = orderedHeaders.map(header => {
        // Format domain names to match UI display
        if (header.includes('.com') || header.includes('.org') || header.includes('.net')) {
          return header; // Keep domain names as they are
        }
        
        // Format other headers with proper capitalization
        return header.charAt(0).toUpperCase() + header.slice(1);
      });
      
      // Create rows in the same order as headers
      const rows = data.map(item => {
        return orderedHeaders.map(key => {
          const value = item[key];
          
          // Format values based on type
          if (value === null || value === undefined) {
            return '-';
          } else if (typeof value === 'number') {
            return value.toLocaleString();
          } else {
            return value;
          }
        });
      });
      
      // Generate table with optimized settings
      autoTable(doc, {
        head: [displayHeaders],
        body: rows,
        startY: 22,
        styles: {
          fontSize: 8, // Smaller font size to fit more columns
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [44, 62, 80],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center', // Center align headers
        },
        columnStyles: {
          // Apply specific styles to columns based on content
          0: { fontStyle: 'bold' }, // First column (usually Keyword) in bold
          // Add column width to ensure all columns fit
          ...orderedHeaders.reduce((styles, _, index) => {
            styles[index] = { 
              cellWidth: index < 5 ? 'auto' : 20 // Standard columns auto width, domain columns fixed
            };
            return styles;
          }, {} as Record<number, { cellWidth: number | 'auto' }>)
        },
        margin: { top: 22, right: 14, bottom: 20, left: 14 },
        didDrawPage: (data) => {
          // Add page numbers for multi-page reports
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10
          );
        }
      });
      
      // Save the PDF
      doc.save(`${fileName}.pdf`);
      console.log('PDF export completed');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw error;
    }
  }
}
